import { CSS } from '@stitches/react';
import { ComponentProps, ElementType } from 'react';
import { cn } from '@/shared/lib/cn';
import { css } from '@/shared/lib/stitches';

export type MediaQuery = 'initial' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | (string & {});
export type MediaStyles = Partial<Record<MediaQuery, CSS>>;

type Props<E extends ElementType> = ComponentProps<E> & {
    element?: E;
    media?: MediaStyles;
};

const mediaQueryMap: Partial<Record<MediaQuery, string>> = {
    xs: '(min-width: 320px)',
    sm: '(min-width: 512px)',
    md: '(min-width: 720px)',
    lg: '(min-width: 910px)',
    xl: '(min-width: 1080px)',
    '2xl': '(min-width: 1368px)',
};

/** Compiles responsive `media` props into a stitches className (mobile-first min-width). */
export const mediaToClassName = (media: MediaStyles = {}) => {
    const { initial, ...restMedia } = { initial: {}, ...media };
    return css({
        ...initial,
        ...Object.fromEntries(
            Object.entries(restMedia).map(([breakpoint, styles]) => [
                `@media ${mediaQueryMap[breakpoint] ? mediaQueryMap[breakpoint] : breakpoint}`,
                styles,
            ]),
        ),
    })().className;
};

export const Layout = <E extends ElementType = 'div'>({
    element,
    media = {},
    className,
    ...props
}: Props<E>) => {
    const Wrapper = element ?? 'div';
    return <Wrapper {...(props as ComponentProps<E>)} className={cn(mediaToClassName(media), className)} />;
};
